import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { VisitRequestWorkflowStep } from '@/types/graphql';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import { DialogType, useDialog } from '../../../dialogs/DialogManager';
import { errorToast } from '../../../toast';
import { getBulkTableQueryForWorkflowStep } from './constants';

export const useUncancelVisitRequest = ({
    visitRequestId,
    workflowStep,
}: {
    visitRequestId: string;
    workflowStep?: VisitRequestWorkflowStep | null;
}) => {
    const { openDialog } = useDialog();
    const tableQuery = getBulkTableQueryForWorkflowStep(workflowStep);

    // Mutations.
    const [doUncancelVisitRequestMutation, { loading, error }] = useMutation(
        UNCANCEL_VISIT_REQUEST_MUTATION,
        {
            variables: { visitRequestId },
            onError: (error) => {
                if (error instanceof Error) {
                    errorToast(error.message);
                }
            },
            refetchQueries: tableQuery ? [ALL_VISIT_COUNTS, tableQuery] : [ALL_VISIT_COUNTS],
        },
    );

    function confirmUncancelVisitRequest() {
        openDialog(DialogType.Confirm, {
            dataTestId: 'confirm-restore-visit-request-dialog',
            title: 'Restore Visit Request',
            message: 'Are you sure you want to restore this canceled visit request?',
            confirmCta: 'Restore',
            cancelCta: 'Back',
            onConfirm: () => {
                void doUncancelVisitRequestMutation();
            },
        });
    }

    return {
        uncancelVisitRequest: ({ confirm = true }: { confirm?: boolean } | undefined = {}) => {
            if (confirm) {
                return confirmUncancelVisitRequest();
            } else {
                return doUncancelVisitRequestMutation();
            }
        },
        loading,
        error,
    };
};

const UNCANCEL_VISIT_REQUEST_MUTATION = gql`
    mutation UncancelVisitRequest($visitRequestId: UUID!) {
        uncancelVisitRequest(visitRequestId: $visitRequestId) {
            id
            state
            workflowStep
            order {
                id
                numActiveOccurrences
            }
        }
    }
`;
